/**
 * Quick Add Modal — variant picker modal triggered from product cards.
 * Extends DialogComponent. Opens on "Choose options" button click.
 * Handles variant selection, price update, and add-to-cart.
 */
import { DialogComponent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class QuickAddModal extends DialogComponent {
  #variants = [];

  connectedCallback() {
    super.connectedCallback();
    const json = this.querySelector('[data-variants-json]');
    if (json) {
      try { this.#variants = JSON.parse(json.textContent); } catch {}
    }
  }

  /**
   * Called via on:click="/onOptionClick" on each option button.
   */
  onOptionClick(event) {
    const btn = event.target.closest('[data-option-index]');
    if (!btn) return;

    const optionIndex = parseInt(btn.dataset.optionIndex, 10);

    // Toggle selected state within this option group
    this.querySelectorAll(`[data-option-index="${optionIndex}"]`).forEach((b) => {
      b.classList.toggle('quick-add-option-btn--selected', b === btn);
    });

    const variant = this.#findVariant(this.#getSelectedOptions());
    this.#updateVariant(variant);
  }

  /**
   * Called via on:click="/onAddToCart" on the submit button.
   */
  onAddToCart() {
    const variantInput = this.querySelector('[data-quick-add-variant-id]');
    const variantId = variantInput ? parseInt(variantInput.value, 10) : null;
    if (!variantId) return;
    this.#addToCart(variantId);
  }

  #getSelectedOptions() {
    const indices = [
      ...new Set(
        [...this.querySelectorAll('[data-option-index]')].map((b) =>
          parseInt(b.dataset.optionIndex, 10)
        )
      ),
    ].sort((a, b) => a - b);

    return indices.map((idx) => {
      const selected = this.querySelector(
        `[data-option-index="${idx}"].quick-add-option-btn--selected`
      );
      return selected ? selected.dataset.value : '';
    });
  }

  #findVariant(options) {
    return (
      this.#variants.find((v) => v.options.every((opt, i) => opt === options[i])) || null
    );
  }

  #updateVariant(variant) {
    const priceEl = this.querySelector('[data-quick-add-price]');
    const submitBtn = this.querySelector('[data-quick-add-submit]');
    const variantInput = this.querySelector('[data-quick-add-variant-id]');

    if (!variant) {
      if (submitBtn) {
        submitBtn.disabled = true;
        const textEl = submitBtn.querySelector('[data-atc-text]') || submitBtn.querySelector('.atc-text');
        if (textEl) textEl.textContent = Theme.translations?.unavailable || 'Unavailable';
      }
      return;
    }

    if (variantInput) variantInput.value = variant.id;

    if (priceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceEl.innerHTML = `<span class="tw:text-error">${this.#formatMoney(variant.price)}</span> <span class="quick-add-dialog__compare-price">${this.#formatMoney(variant.compare_at_price)}</span>`;
      } else {
        priceEl.textContent = this.#formatMoney(variant.price);
      }
    }

    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      const textEl = submitBtn.querySelector('[data-atc-text]') || submitBtn.querySelector('.atc-text');
      if (textEl) {
        textEl.textContent = variant.available
          ? Theme.translations?.add_to_cart || 'Add to cart'
          : Theme.translations?.sold_out || 'Sold out';
      }
    }
  }

  async #addToCart(variantId) {
    const submitBtn = this.querySelector('[data-quick-add-submit]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
    }

    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      const sectionId = cartDrawer?.dataset.sectionId || '';

      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionId,
        sections_url: window.location.pathname,
      });

      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.errors) {
        if (submitBtn) {
          submitBtn.classList.remove('is-loading');
          submitBtn.disabled = false;
        }
        return;
      }

      document.dispatchEvent(
        new CartAddEvent(data, null, {
          sections: data.sections,
          source: 'quick-add-modal',
        })
      );

      // Success animation, then close
      if (submitBtn) {
        submitBtn.classList.remove('is-loading');
        submitBtn.classList.add('is-added');
        setTimeout(() => {
          submitBtn.classList.remove('is-added');
          submitBtn.disabled = false;
          this.closeDialog();
        }, 1500);
      }
    } catch (err) {
      console.error('Quick add modal error:', err);
      if (submitBtn) {
        submitBtn.classList.remove('is-loading');
        submitBtn.disabled = false;
      }
    }
  }

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    const format = Theme.moneyFormat || '{{amount}} €';
    return format.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount);
  }
}

if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', QuickAddModal);
}
